import React, { useCallback, useMemo, useState } from 'react';

import { mentionType } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';
import type { PlateEditor } from 'platejs/react';
import { getEditorSearchStyles } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/styles';
import type { ReportColumnMentionElement } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorMention/utils';
import { insertMentionAt } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorMention/utils';
import { AlineButton, AlineCheckbox } from 'src/components/generic';
import { SearchInput } from 'src/components/SearchInput';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import { ColumnIcon } from './ColumnIcon';
import type { ColumnOption } from './ReportColumnMentionContext';
import { useReportColumnMentionContext } from './ReportColumnMentionContext';

const useStyles = makeStyles()(theme => {
  const sharedSearchStyles = getEditorSearchStyles(theme);
  return {
    ...sharedSearchStyles,
    columnName: {
      ...getTypographyForSx(theme.typography.body2),
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
    content: {
      boxSizing: 'border-box' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      flexGrow: 1,
      height: 150,
      overflowY: 'auto' as const,
      width: 280,
    },
    footer: {
      alignItems: 'center',
      borderTop: `1px solid ${theme.appColors.rule}`,
      display: 'flex',
      justifyContent: 'flex-end',
      padding: theme.spacing(1),
    },
    label: {
      fontWeight: 600,
      padding: theme.spacing(0.5, 1),
    },
    menuItem: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      alignItems: 'center',
      borderRadius: 8,
      cursor: 'pointer',
      display: 'flex',
      fontSize: theme.typography.body2.fontSize,
      gap: theme.spacing(1),
      margin: theme.spacing(0, 0.5),
      padding: theme.spacing(0.5, 1),
      userSelect: 'none' as const,
    },
    menuItemSelected: {
      backgroundColor: theme.palette.action.selected,
    },
    searchWrapper: {
      padding: theme.spacing(0, 1),
    },
  };
});

interface ReportColumnMentionMenuProps {
  editor: PlateEditor;
  onClose: () => void;
}

export function ReportColumnMentionMenu({
  editor,
  onClose,
}: ReportColumnMentionMenuProps): JSX.Element {
  const { classes } = useStyles();
  const [search, setSearch] = useState('');
  const { columns } = useReportColumnMentionContext();
  const [selected, setSelected] = useState<ColumnOption[]>([]);
  const filtered = useMemo(
    () =>
      matchSorter(columns, search, {
        keys: ['displayName', 'id'] satisfies (keyof ColumnOption)[],
      }),
    [columns, search]
  );
  const selectedIds = useMemo(() => new Set(selected.map(c => c.id)), [selected]);

  const handleToggle = useCallback((column: ColumnOption) => {
    setSelected(prev =>
      prev.some(c => c.id === column.id) ? prev.filter(c => c.id !== column.id) : [...prev, column]
    );
  }, []);

  const handleInsert = useCallback(() => {
    for (const column of selected) {
      const mentionNode: ReportColumnMentionElement = {
        children: [{ text: '' }],
        columnId: column.id,
        columnName: column.displayName,
        mentionTypeName: 'report-column',
        type: mentionType,
        value: column.displayName,
      };
      insertMentionAt(editor, { mentionNode });
    }
    onClose();
  }, [editor, onClose, selected]);

  return (
    <>
      <Typography className={classes.label} variant="subtitle2">
        Report columns
      </Typography>
      <div className={classes.searchWrapper}>
        <SearchInput
          className={classes.searchInput}
          minWidth="100%"
          placeholder="Search columns..."
          search={search}
          setSearch={setSearch}
          onMouseDown={e => e.stopPropagation()}
        />
      </div>
      <div className={classes.content}>
        {filtered.map(col => (
          <div
            key={col.id}
            className={clsx(classes.menuItem, selectedIds.has(col.id) && classes.menuItemSelected)}
            onClick={e => {
              e.stopPropagation();
              handleToggle(col);
            }}
          >
            <AlineCheckbox
              checked={selectedIds.has(col.id)}
              size="small"
              sx={{ p: 0 }}
              onMouseDown={e => e.preventDefault()}
            />
            <span
              style={{
                alignItems: 'center',
                display: 'flex',
                flexShrink: 0,
                justifyContent: 'center',
                width: 20,
              }}
            >
              <ColumnIcon columnId={col.id} />
            </span>
            <Typography className={classes.columnName} component="span">
              {col.displayName}
            </Typography>
          </div>
        ))}
        {!filtered.length && (
          <Typography
            sx={{
              alignItems: 'center',
              color: 'text.secondary',
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
            }}
            variant="body2"
          >
            No columns found
          </Typography>
        )}
      </div>
      <div className={classes.footer}>
        <AlineButton
          color="primary"
          disabled={selected.length === 0}
          size="small"
          variant="contained"
          onClick={handleInsert}
        >
          Insert
        </AlineButton>
      </div>
    </>
  );
}
